import { endpoints } from '@api'
import {
  Headphones,
  InfoIcon,
  InPersonFilled,
  NoHeadsetIcon,
  Online,
  PersonAddIcon,
  SearchIcon
} from '@blueprinthq/joy'
import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  Tooltip,
  useToast
} from '@chakra-ui/react'
import {
  Loading,
  SearchSelect,
  UsageBasedPricingWarningBanner
} from '@components'
import { Mic, ShareScreen } from '@components/icons'
import { FlagsmithFeatures } from '@constants/flagsmith'
import { datadogLogs } from '@datadog/browser-logs'
import {
  AudioLevel,
  MicrophoneSelector
} from '@handlers/sessions/components/microphone-selector'
import { CustomSwitch } from '@handlers/sessions/components/shared/custom-switch'
import { TelehealthToggleTooltipContent } from '@handlers/sessions/components/shared/warning-tooltip'
import { TELEHEALTH_TOGGLE_MESSAGES } from '@handlers/sessions/constants'
import { shouldShareSystemAudio } from '@handlers/sessions/util'
import { useAudioCapture, useExperienceManager, useExtension } from '@hooks'
import { trackEvent } from '@lib/clinician-tracking'
import { useStoreState } from 'easy-peasy'
import flagsmith from 'flagsmith'
import { throttle } from 'lodash'
import React, { useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'
import { components } from 'react-select'
import type { StoreModel } from 'src/store/types'
import {
  useOrganizationControllerGetSessionCredit,
  usePatientControllerGetPatients,
  useSessionControllerPatchSession,
  useSessionControllerPostSession
} from '~/clinician-api'
import {
  isChromiumBrowser,
  isWindows
} from '../../../context/sessions-context/utils'
import { IconSelect } from '../../sessions/components'
import { MAC_TELEHEALTH_DEFAULT_WARNING_MESSAGE } from './constants'

interface Props {
  sessionId: string | undefined
  setSessionId: (sessionId: string) => void
}

const Control = (props: any) => (
  <components.Control {...props}>
    <SearchIcon name="search" style={{ marginLeft: 16, marginRight: 8 }} />
    {props.children}
  </components.Control>
)

export const SelectClient = ({ sessionId, setSessionId }: Props) => {
  const { ehrClientNames, ehrClientName } = useExtension()
  const { user } = useStoreState((state: StoreModel) => state.auth)
  const [searchInput, setSearchInput] = useState('')
  const [selectedClient, setSelectedClient] = useState<
    { value: string; label: string } | undefined
  >()
  const [isTelehealth, setIsTelehealth] = useState(false)
  const history = useHistory()
  const {
    testAudioInputs,
    hasMicAccess,
    hasResolvedInputDevicePermissions,
    isUsingHeadphones,
    setIsUsingHeadphones,
    qualityToggle,
    setQualityToggle,
    promptForDevicePermissions,
    permissionStatus,
    telehealthAudioSource,
    setTelehealthAudioSource,
    isTelehealthSourceRequiredEnabled,
    startRecording
  } = useAudioCapture()
  const shareSystemAudio = shouldShareSystemAudio({
    isWindows: isWindows(),
    isFirefox: false,
    isTelehealth,
    isUsingHeadphones,
    isMobile,
    telehealthAudioSource,
    qualityToggle,
    isTelehealthSourceRequiredEnabled
  })

  const {
    showRulaUI,
    isAudioShareSourceDropdownEnabled,
    isDocumentationAutomationEnabled,
    usageBasedPricingEnabled
  } = useExperienceManager()
  const { mutateAsync: createSessionDraft } = useSessionControllerPostSession()
  const { mutateAsync: patchSession } = useSessionControllerPatchSession()
  const toast = useToast()

  const { data: sessionCredits } = useOrganizationControllerGetSessionCredit(
    user?.clinic?.organization_id || '',
    {
      query: {
        enabled: !!(usageBasedPricingEnabled && user?.clinic?.organization_id)
      }
    }
  )

  useEffect(() => {
    promptForDevicePermissions()
    testAudioInputs()
  }, [hasResolvedInputDevicePermissions])

  const startSession = async (clientId: string) => {
    const sessionDraft = await createSessionDraft({
      data: {
        patientId: clientId,
        isExtension: true,
        isChime: false,
        extensionInitiatedFrom: document.referrer,
        isDictation: false,
        isTelehealth,
        resumeInProgress: true,
        isAssistEnabled: false,
        isUsingHeadphones
      }
    })

    await patchSession({ id: sessionDraft.id, data: { isTelehealth } })

    setSessionId(sessionDraft.id)
  }

  const isInternalDemoOrg = flagsmith.hasFeature(
    FlagsmithFeatures.SHOW_IS_DEMO_CLIENTS_IN_START_SESSION
  )

  const { data: preferredClientList, isLoading: isPreferredClientListLoading } =
    useQuery(
      [endpoints.getPreferredClientList.getCacheId(), ehrClientNames],
      () =>
        endpoints.getPreferredClientList.request({
          clinicianId: user!.id,
          preferredClientInfo: {
            clientNames: ehrClientNames
          }
        }),
      {
        initialData: { clientsFromEHR: [], clientsWithSessionLastWeek: [] },
        select: (data: any) => ({
          clientsFromEHR: data.clientsFromEHR.filter(
            (n: any) => !n.isDemo || isInternalDemoOrg || showRulaUI
          ),
          clientsWithSessionLastWeek: data.clientsWithSessionLastWeek.filter(
            (n: any) =>
              (!n.isDemo || isInternalDemoOrg || showRulaUI) &&
              !data.clientsFromEHR.map((c: any) => c.id).includes(n.id)
          )
        })
      }
    )

  const { data: defaultList, isLoading: isDefaultListLoading } = useQuery(
    [endpoints.getClientList.getCacheId(), user?.id],
    () =>
      endpoints.getClientList.request({
        limit: 50,
        status: 'active',
        clinicianId: user!.id
      }),
    {
      initialData: [],
      select: (data: any) =>
        data.filter((n: any) => !n.isDemo || isInternalDemoOrg || showRulaUI)
    }
  )

  const {
    refetch,
    data: searchResults,
    isLoading: isSearchLoading
  } = useQuery(
    [
      endpoints.getPatientSearch.getCacheId(),
      'session_client_search',
      searchInput
    ],
    () =>
      endpoints.getPatientSearch.request({
        search: searchInput,
        include_discharged: false
      }),
    {
      enabled: false,
      initialData: [],
      select: (data: any) =>
        data
          .filter((n: any) => !n.is_demo || isInternalDemoOrg || showRulaUI)
          .map((n: any) => ({
            id: n.id,
            firstName: n.first_name,
            lastName: n.last_name
          }))
    }
  )

  const { data: autoSelectedClient } = usePatientControllerGetPatients(
    {
      clinicianId: user!.id,
      firstName: ehrClientName?.split(' ')[0]!,
      lastName: ehrClientName?.split(' ')?.[1]!,
      status: 'active',
      limit: 1
    },
    {
      query: {
        enabled: !!ehrClientName
      }
    }
  )

  useEffect(() => {
    if (searchInput?.length >= 1) {
      throttledSearch()
    }
  }, [searchInput])

  useEffect(() => {
    const client = autoSelectedClient?.[0]
    if (client) {
      startSession(client.id)
      setSelectedClient({ value: client.id, label: ehrClientName as string })
    } else {
      setSelectedClient(undefined)
    }
  }, [autoSelectedClient])

  const showAudioSourceDropdown =
    isChromiumBrowser() &&
    isTelehealth &&
    !isUsingHeadphones &&
    isAudioShareSourceDropdownEnabled

  useEffect(() => {
    // Set default values when dropdowns are shown for the first time
    if (showAudioSourceDropdown) {
      if (!telehealthAudioSource) {
        setTelehealthAudioSource('browser')
      }
      if (!qualityToggle) {
        setQualityToggle(true)
      }
    } else {
      // Reset values when dropdowns are hidden
      setTelehealthAudioSource(null)
      setQualityToggle(null)
    }
  }, [showAudioSourceDropdown])

  const throttledSearch = throttle(() => refetch(), 150)

  const getTooltipMessage = (): string => {
    if (isUsingHeadphones) {
      return MAC_TELEHEALTH_DEFAULT_WARNING_MESSAGE
    }
    if (isWindows()) {
      return TELEHEALTH_TOGGLE_MESSAGES.NO_HEADPHONES_HIGH_QUALITY_EXTENSION
    }
    if (qualityToggle === true) {
      return TELEHEALTH_TOGGLE_MESSAGES.NO_HEADPHONES_HIGH_QUALITY_EXTENSION
    }
    return TELEHEALTH_TOGGLE_MESSAGES.NO_HEADPHONES_LOW_QUALITY
  }

  const handleStartRecording = async () => {
    if (!selectedClient)
      return toast({
        title: 'Error',
        description: 'Select a client to start recording',
        status: 'error',
        isClosable: true,
        duration: 2000
      })

    if (!hasMicAccess)
      return toast({
        title: 'Error',
        description: 'Enable you microphone to start recording',
        status: 'error',
        isClosable: true,
        duration: 2000
      })

    if (!isDocumentationAutomationEnabled) {
      datadogLogs.logger.warn(
        '[Extension] Documentation automation access denied',
        {
          userId: user?.id
        }
      )
      return toast({
        title: 'Access Required',
        description:
          'You need a documentation automation subscription to use this feature',
        status: 'error',
        isClosable: true,
        duration: 3000
      })
    }

    await startRecording({
      noteType: 'soap',
      isTelehealth,
      isUsingHeadphones,
      telehealthAudioSource,
      isHighQualityAudio: qualityToggle
    })

    const isAutoSelectedClient =
      autoSelectedClient?.[0]?.id === selectedClient.value

    const isSuggestedClient = preferredClientList?.clientsFromEHR?.some(
      (c: any) => c.id === selectedClient.value
    )
    const isLastWeekClient =
      preferredClientList?.clientsWithSessionLastWeek?.some(
        (c: any) => c.id === selectedClient.value
      )

    if (isAutoSelectedClient) {
      trackEvent(`Session -> Selected Client - Auto Suggested Client`)
    } else if (isSuggestedClient) {
      const clientPositionInList =
        preferredClientList?.clientsFromEHR?.findIndex(
          (c: any) => c.id === selectedClient.value
        )
      trackEvent(
        `Session -> Selected Client - Suggested Client ${
          clientPositionInList + 1
        }`
      )
    } else if (isLastWeekClient) {
      trackEvent('Session -> Selected Client - Session Last Week')
    } else {
      trackEvent('Session -> Selected Client - All Clients')
    }

    trackEvent('Session -> Started Recording')

    history.push(
      `/extension/patient/${selectedClient.value}/session/${sessionId}`
    )
  }

  const patients = searchInput?.length
    ? searchResults
    : defaultList.filter(
        // don't show clients that are in the preferred client list
        (p: any) =>
          !preferredClientList!.clientsFromEHR
            .map((c: any) => c.id)
            .includes(p.id) &&
          !preferredClientList!.clientsWithSessionLastWeek
            .map((c: any) => c.id)
            .includes(p.id)
      )

  if (isDefaultListLoading || isPreferredClientListLoading) {
    return (
      <Box h="70vh" color="white">
        <Loading />
      </Box>
    )
  }

  const selectCLientOptions = [
    {
      label: '',
      options: [
        {
          value: 'newClient',
          label: 'Add a new client'
        }
      ]
    },
    ...(preferredClientList?.clientsFromEHR?.length && !searchInput
      ? [
          {
            label: 'Suggested clients',
            options: preferredClientList?.clientsFromEHR.map((c: any) => ({
              value: c.id,
              label: `${c.firstName} ${c.lastName}`
            }))
          }
        ]
      : []),
    ...(preferredClientList?.clientsWithSessionLastWeek?.length && !searchInput
      ? [
          {
            label: 'Clients you met with last week',
            options: preferredClientList?.clientsWithSessionLastWeek.map(
              (c: any) => ({
                value: c.id,
                label: `${c.firstName} ${c.lastName}`
              })
            )
          }
        ]
      : []),
    {
      label:
        (preferredClientList?.clientsFromEHR?.length ||
          preferredClientList?.clientsWithSessionLastWeek?.length) &&
        !searchInput
          ? 'All clients'
          : '',
      options: patients.map((p: any) => ({
        value: p.id,
        label: `${p.firstName} ${p.lastName}`
      }))
    }
  ]

  return (
    <Flex
      h="100%"
      backgroundColor="primary"
      flexDirection="column"
      justifyContent="space-between"
      id="bp-extension-loaded"
      p="xsmall"
    >
      <Box>
        {usageBasedPricingEnabled &&
          (sessionCredits?.freeCreditsRemaining || 0) +
            (sessionCredits?.creditsRemaining || 0) ===
            0 && (
            <UsageBasedPricingWarningBanner
              type="out-of-sessions"
              isExtension
            />
          )}
        <Text color="white">Client:</Text>
        <SearchSelect
          size="small"
          placeholder="Select or add a client"
          options={selectCLientOptions}
          value={selectedClient}
          filterOption={null}
          onSearch={setSearchInput}
          onChange={async (option: any) => {
            if (option.value === 'newClient') {
              history.push('/start-session/new-client')
            } else {
              await startSession(option.value)
              setSelectedClient(option)
            }
          }}
          Components={{
            Option: (props: any) => (
              <components.Option {...props}>
                <Flex alignItems="center">
                  {props.data.value === 'newClient' && (
                    <Box mt="xsmall" mr="xsmall">
                      <PersonAddIcon />
                    </Box>
                  )}
                  {props.data.label}
                </Flex>
              </components.Option>
            ),
            Control
          }}
          errors={null}
        />
        <Box mt="xsmall" mb="xsmall">
          <MicrophoneSelector isExtension />
          {permissionStatus === 'denied' && (
            <Text color="white" mt="xsmall">
              Please allow access in your browser and refresh the page.
            </Text>
          )}
        </Box>
        <Text color="white">Test your microphone:</Text>
        <Flex
          alignItems="center"
          border="1px solid #C9C9C9"
          w="100%"
          bg="white"
          borderRadius="6px"
          h="40px"
          pl="small"
          mt="xsmall"
          mb="xsmall"
        >
          <AudioLevel />
        </Flex>
        <Text color="white">Setting:</Text>
        <Box mb="xsmall" mt="xsmall">
          <IconSelect
            onChange={async (value: string) => {
              if (sessionId)
                await patchSession({
                  id: sessionId,
                  data: { isTelehealth: value === 'telehealth' }
                })

              setIsTelehealth(value === 'telehealth')
            }}
            selectedValue={isTelehealth ? 'telehealth' : 'inPerson'}
            options={[
              { value: 'telehealth', title: 'Telehealth', icon: <Online /> },
              {
                value: 'inPerson',
                title: 'In-Person',
                icon: <InPersonFilled />
              }
            ]}
            height="40px"
          />
        </Box>
        {isTelehealth && (
          <>
            <Flex>
              <Text color="white">Headphones:</Text>
              <Tooltip
                label={
                  <TelehealthToggleTooltipContent
                    message={getTooltipMessage()}
                  />
                }
              >
                <Box ml={1} w="20px" h="20px">
                  <InfoIcon fill="white" />
                </Box>
              </Tooltip>
            </Flex>
            <Box mb="xsmall" mt="xsmall">
              <IconSelect
                onChange={async (value: string) => {
                  const isUsingHeadphones = value === 'headphones'
                  setIsUsingHeadphones(isUsingHeadphones)
                  if (sessionId)
                    await patchSession({
                      id: sessionId as string,
                      data: { isUsingHeadphones }
                    })
                }}
                selectedValue={
                  isUsingHeadphones ? 'headphones' : 'noHeadphones'
                }
                options={[
                  {
                    value: 'headphones',
                    title: 'Using headphones',
                    icon: <Headphones />
                  },
                  {
                    value: 'noHeadphones',
                    title: 'Not using headphones',
                    icon: <NoHeadsetIcon />
                  }
                ]}
                height="40px"
              />
            </Box>
            {/* Only show the switch if on chromium browser, telehealth mode, and not using headphones */}
            {showAudioSourceDropdown && (
              <Box
                mb="12px"
                mt="12px"
                w="100%"
                display="flex"
                bg="white"
                border="1px solid #C9C9C9"
                borderRadius="6px"
                p="xsmall"
                alignItems="center"
                gap="xsmall"
              >
                <CustomSwitch
                  isChecked={qualityToggle ?? true}
                  onChange={() => setQualityToggle(!qualityToggle)}
                />
                <Text color="black">Share system audio (highest quality)</Text>
              </Box>
            )}
          </>
        )}
      </Box>
      <Button
        aria-label="Start Recording"
        bg="black"
        onClick={handleStartRecording}
        isFullWidth
        size="lg"
        borderRadius="4px"
        background="#282828"
        m={0}
        disabled={
          usageBasedPricingEnabled &&
          (sessionCredits?.freeCreditsRemaining || 0) +
            (sessionCredits?.creditsRemaining || 0) ===
            0
        }
      >
        <HStack spacing={2}>
          {shareSystemAudio ? (
            <ShareScreen fill="#68E19F" />
          ) : (
            <Mic fill="#68E19F" />
          )}
          <Text fontWeight="bold" color="white">
            {shareSystemAudio
              ? 'Share audio + start recording'
              : 'Start recording'}
          </Text>
        </HStack>
      </Button>
    </Flex>
  )
}
